import { useTranslation } from "react-i18next";
import React, { ReactElement, useEffect, useState } from "react";
import { successToast, warningToast, errorToast, infoToast, DownloadMenu } from "@idwal/idwal-react-components";
import { sendGAEvent } from "../services/AnalyticsService";
import { getDownloads } from "../queries/downloadQueries";
import { useParams } from "react-router";
import { DocumentData } from "../types/DocumentData";
import { FULL_REPORT_DOC_NAME, REPORT_ZIP_NAME, TECHNICAL_REPORT_NAME } from "../constants/inspectionElementNames";
import {GA_BUTTON_EVENT, GA_DOCS_AND_IMAGES_DOWNLOAD, GA_PDF_DOWNLOAD, GA_TECHNICAL_REPORT_DOWNLOAD} from "../constants/GaEvents";
import downloadFile from "../queries/clientDownloader";
import AuthToken from '../queries/AuthToken'
import {Claims} from "../types/Claims";
import IdwalAdobeViewSDKClient from "@idwal/idwal-utilities/lib/IdwalAdobeViewSDKClient/IdwalAdobeViewSDKClient";
import {AppContext} from "../App";
import {AppContextType} from "../types/AppContextType";

interface DownloadableDocument {
    label: string;
    key: string;
    file: DocumentData;
}

const isDownloadable = (file?: DocumentData) => Boolean(file?.value?.url);

export const DownloadAndShare = (): ReactElement => {
    const { t } = useTranslation("locale");
    const [downloadOption, setDownloadOption] = useState("");
    const { inspectionId } = useParams();
    const viewSDKClient = new IdwalAdobeViewSDKClient();
    const { setRestrictedView } = React.useContext(AppContext) as AppContextType;


    const { data, refetch } = getDownloads(inspectionId ?? "");

    useEffect(() => {
        if (data?.message) {
            infoToast(data.message);
        }
    },[data]);

    setRestrictedView(data?.limitDownloadsToSupportingDocumentsOnly as boolean);

    // A document is only offered once the API has given us something to fetch.
    // An ID Condition Inspection has no Part B, so it has no technical report -
    // the option is left out rather than failing when the user selects it.
    const availableDocuments = [
        { label: "Idwal Report PDF", key: FULL_REPORT_DOC_NAME, file: data?.fullReport },
        { label: "Supporting Documents and Images", key: REPORT_ZIP_NAME, file: data?.zipElement },
        { label: "Technical Report", key: TECHNICAL_REPORT_NAME, file: data?.technicalReportElement },
    ].filter((document): document is DownloadableDocument => isDownloadable(document.file));

    const items = (
        data?.limitDownloadsToSupportingDocumentsOnly &&
        process.env.REACT_APP_RESTRICT_DOWNLOAD_OPTIONS_FOR_SELLERS
    ) ? [] : availableDocuments.map(({ label, key }) => ({ label, key }));

    const token = AuthToken();
    let customerName = "";
    let organisation = "";
    let claims;

    if (token) {
        claims = JSON.parse(atob(token.split('.')[1])) as Claims;
        customerName = claims.name;
        organisation = claims.organisation;
    }

    const onAgreeAndDownload = async () => {
        successToast(t("report.downloadStarted"));
        await downloadItem();
    };

    function handleGaEvent(documentKey: string) {
        const actionMap: { [name: string]: string } = {
            [FULL_REPORT_DOC_NAME]: GA_PDF_DOWNLOAD,
            [REPORT_ZIP_NAME]: GA_DOCS_AND_IMAGES_DOWNLOAD,
            [TECHNICAL_REPORT_NAME]: GA_TECHNICAL_REPORT_DOWNLOAD
        }

        const action: string = actionMap[documentKey];

        if (action) {
            sendGAEvent(GA_BUTTON_EVENT, action);
        }
    }

    async function downloadItem() {
        handleGaEvent(downloadOption);
        const documentToDownload = availableDocuments.find(e => e.key === downloadOption);

        // Unreachable from the menu, which only lists available documents, but
        // kept so a stale selection still reports rather than throwing.
        if (!documentToDownload) {
            const warnText = t("downloads.noDownload") + downloadOption;
            console.warn(warnText);
            warningToast(warnText);
            return;
        }

        const { url, fileName } = documentToDownload.file.value;

        try {
            const response = await fetch(url);
            if (!response || response.status !== 200) {
                console.error(response.statusText + " - " + url)
                warningToast(t("downloads.downloadFailed"));
                return;
            }

            downloadFile(url, fileName);

        } catch (error) {
            console.error(error);
            warningToast(t("downloads.downloadFailed"));
        }
    }

    const termsAndConditions = [
        `${t("report.tsAndCs_1", { customerName, organisation })}`,
        `${t("report.tsAndCs_2")}`,
        `${t("report.tsAndCs_3")}`,
        `${t("report.tsAndCs_4")}`,
    ];

    const previewFile = () => {
        refetch().then(
            (response) => {
                const fullReport = response.data?.fullReport;

                if (fullReport) {
                    viewSDKClient.ready().then(() => {
                        viewSDKClient.previewFile(
                            process.env.REACT_APP_ADOBE_API_KEY,
                            fullReport.value.url,
                            fullReport.value.fileName,
                            fullReport.key,
                            {
                                showAnnotationTools: false,
                                showDownloadPDF: false,
                                showPrintPDF: false,
                                embedMode: "LIGHT_BOX"
                            }
                        );
                        viewSDKClient.registerEventsHandler();
                    });
                } else {
                    if (response.data?.message) {
                        warningToast(response.data.message);
                    }
                    console.error("Full report not in response");
                }
            }
        ).catch((e) => {
            console.error("Error fetching document: " + e);
            errorToast(t("report.errorMessage"));
        });
    }

    return (
        <DownloadMenu
            downloads={items}
            onAgreeAndDownload={onAgreeAndDownload}
            setDownloadOption={setDownloadOption}
            onPeview={previewFile}
            modalHeader={t("report.reportSharingTsAndCs")}
            termsAndConditions={termsAndConditions}
            termsAndConditionsLink={
                "https://www.idwalmarine.com/terms-and-conditions/inspection-reports"
            }
            previewMode={
                process.env.REACT_APP_PREVIEW_PDF_ENABLED === "1"
            }
        />
    );
};
